import type { IntakeFormConfigurationResponse } from '@store/generated/intake-hub';

export type IntakeReminderVariant = 'start' | 'finish';

type ReminderConfiguration = Pick<
  IntakeFormConfigurationResponse,
  | 'can_manage_services'
  | 'clinic_intake_submitted'
  | 'completed_pages'
  | 'enrolled_services'
  | 'last_change'
  | 'products'
  | 'role'
>;

/** Mirrors the server's `_required_review_pages`: confirming these pages records the submission. */
function requiredReviewPages(role: ReminderConfiguration['role']): ('M6' | 'P6')[] {
  if (role === 'clinic_manager') return ['M6'];
  if (role === 'manager_who_also_practices') return ['M6', 'P6'];
  return ['P6'];
}

/**
 * Which reminder the signed-in user owes, from their own intake configuration.
 *
 * A user owes intake work when they hold a journey of their own (a rostered
 * clinician's `products`) or manage a practice that holds intake services.
 * Once a colleague has confirmed the clinic review, the clinic half is done for
 * every manager; only a manager's own provider review can still be owed.
 * The reminder ends at submission, not at staff verification.
 */
export function resolveIntakeReminder(
  configuration: ReminderConfiguration | undefined,
): IntakeReminderVariant | null {
  if (!configuration) return null;
  const hasOwnJourney = configuration.products.length > 0;
  const managesEnrolledPractice =
    Boolean(configuration.can_manage_services) &&
    Boolean(configuration.enrolled_services?.length);
  const clinicIntakeSubmitted = Boolean(configuration.clinic_intake_submitted);
  if (!hasOwnJourney && (!managesEnrolledPractice || clinicIntakeSubmitted)) return null;
  if (!configuration.last_change) return 'start';
  const completedPages = new Set<string>(configuration.completed_pages ?? []);
  if (clinicIntakeSubmitted) completedPages.add('M6');
  const submitted = requiredReviewPages(configuration.role).every(page => completedPages.has(page));
  return submitted ? null : 'finish';
}

/** The hub and staff tooling (`/admin`, `/admin-v2`) never show the reminder. */
export function isIntakeReminderHiddenOnPath(pathname: string): boolean {
  return /^\/(intake|admin|admin-v2)(\/|$)/.test(pathname);
}

/** Auth0 staff roles, as the admin routes and `IntakeStediConnection` read them. */
export function hasStaffRole(roles: unknown): boolean {
  return Array.isArray(roles) && roles.some(role => role === 'Admin' || role === 'Moderator');
}
